// Single source of truth for the Real Time Report's status cards.
//
// The cards are deliberately NOT 1:1 with `user_type` — several statuses merge into one
// card (an inbound and an outbound call are both "On Call"). Now that clicking a card
// filters the listing, the types a card counts and the types it filters to must come from
// the same place, or a card can show 3 and filter down to 2.
//
// On type 9: going on break SIP-unregisters the agent as part of entering the break
// (CallingSidebar.jsx `onSelectBreak` -> `onUnregisterCall`), so 9 belongs with 5 under
// Break — which is how the counts have always been summed. ReportList labels those rows
// "Unregister", a more specific truth about the same agent, so expect the Break filter to
// list rows reading Unregister.
//
// Type 10 (PBX Play) has no card and is excluded from Total. That is pre-existing — it was
// dropped from the counts before the total was summed — and is left alone deliberately so
// this change doesn't move numbers supervisors already read. Those agents still show in the
// unfiltered listing.

export const TOTAL_STATUS_KEY = 'total';

// Order here is the order the cards render in, and matches what the page showed before.
// `types: null` marks the card that clears the filter rather than applying one.
export const REALTIME_STATUS_CARDS = [
  { key: TOTAL_STATUS_KEY, title: 'Total', color: '#122335', icon: 'Phone', types: null },
  { key: 'onCall', title: 'On Call', color: '#008000', icon: 'PhoneForwarded', types: [1, 4] },
  { key: 'idle', title: 'Idle', color: '#2cabe3', icon: 'Slash', types: [0] },
  { key: 'wrapUp', title: 'Wrap Up', color: '#775dd0', icon: 'CheckCircle', types: [2] },
  { key: 'break', title: 'Break', color: '#ffb22b', icon: 'Coffee', types: [5, 9] },
  { key: 'dialing', title: 'Dialing', color: '#800080', icon: 'Volume2', types: [6, 8] },
  { key: 'ringing', title: 'Ringing', color: '#6376ff', icon: 'Rss', types: [7] }
];

export const getStatusCard = (key) => REALTIME_STATUS_CARDS.find((card) => card.key === key);

// `type` and `user_type` both arrive from the API as strings — compare numerically.
export const filterRowsByStatus = (rows = [], statusKey) => {
  const card = statusKey ? getStatusCard(statusKey) : null;
  if (!card?.types) return rows;
  return rows.filter((row) => card.types.includes(Number(row.user_type)));
};

// Card counts, summed from /realtime/count rows of the shape { type, total_count }.
export const buildStatusCounts = (data = []) => {
  const byType = data.reduce((acc, item) => {
    const type = Number(item.type);
    acc[type] = (acc[type] || 0) + (parseInt(item.total_count, 10) || 0);
    return acc;
  }, {});

  const counts = REALTIME_STATUS_CARDS.reduce((acc, card) => {
    if (card.types) {
      acc[card.key] = card.types.reduce((sum, type) => sum + (byType[type] || 0), 0);
    }
    return acc;
  }, {});

  counts[TOTAL_STATUS_KEY] = Object.values(counts).reduce((sum, count) => sum + count, 0);
  return counts;
};
